import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Info,
  Calendar,
  User,
  MessageSquare,
  X,
  Eye,
  History,
  CheckCircle2,
  Clock,
  Loader2,
  AlertCircle,
  Save,
  Send
} from 'lucide-react';
import styles from './ScheduleInspection.module.css';
import Footer from '../../components/Common/Footer/Footer';
import NotFound from '../Error/NotFound';

const ScheduleInspection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = !!id;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data State
  const [poOptions, setPoOptions] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [recentInspections, setRecentInspections] = useState([]);

  // PO Pagination State
  const [poPage, setPoPage] = useState(1);
  const [poLastPage, setPoLastPage] = useState(1);
  const [isPoLoading, setIsPoLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    agency_type: 'SMC',
    notified_date: new Date().toISOString().split('T')[0],
    actual_inspection_date: '',
    contact_person_name: '',
    remarks: ''
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    fetchPOOptions(1);
    if (isEditMode) {
      fetchInspectionDetails();
      fetchInspectionHistory();
    } else {
      fetchRecentInspections();
    }
  }, [id]);

  const fetchPOOptions = async (pageToFetch = 1) => {
    if (isPoLoading) return;
    try {
      setIsPoLoading(true);
      const response = await api.get('/purchase-orders', {
        params: { page: pageToFetch },
        skipLoader: true
      });
      if (response.data.success) {
        const rawData = response.data.data;
        const poList = rawData?.data || [];
        const formatted = poList.map(po => ({
          value: po.id,
          label: po.po_number,
          ...po
        }));

        setPoPage(rawData?.current_page || pageToFetch);
        setPoLastPage(rawData?.last_page || 1);

        if (pageToFetch === 1) {
          setPoOptions(prev => {
            // Keep any selected PO item that might have been injected during edit mode load
            const existingSelected = prev.filter(p => !formatted.some(f => f.value === p.value));
            return [...existingSelected, ...formatted];
          });
        } else {
          setPoOptions(prev => {
            const existingIds = new Set(prev.map(p => p.value));
            const uniqueNew = formatted.filter(f => !existingIds.has(f.value));
            return [...prev, ...uniqueNew];
          });
        }
      }
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setIsPoLoading(false);
    }
  };

  const handlePOScrollToBottom = () => {
    if (poPage < poLastPage && !isPoLoading) {
      fetchPOOptions(poPage + 1);
    }
  };

  const fetchInspectionDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/inspections/${id}`);
      if (response.data.success) {
        const inspection = response.data.data;
        setFormData({
          agency_type: inspection.agency_type,
          notified_date: inspection.notified_date,
          actual_inspection_date: inspection.actual_inspection_date || '',
          contact_person_name: inspection.contact_person_name || '',
          remarks: inspection.remarks || ''
        });

        // Enrich PO details with vendor and project information from the top-level response
        if (inspection.purchase_order) {
          const enrichedPo = {
            ...inspection.purchase_order,
            vendor: inspection.vendor,
            project: inspection.project
          };
          const poOptionObj = {
            value: enrichedPo.id,
            label: enrichedPo.po_number,
            ...enrichedPo
          };
          setSelectedPO(poOptionObj);
          setPoDetails(enrichedPo);

          // Ensure selected PO is in options list in edit mode
          setPoOptions(prev => {
            if (!prev.some(opt => opt.value === enrichedPo.id)) {
              return [poOptionObj, ...prev];
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching inspection details:', error);
      toast.error('Failed to load inspection details');
      navigate('/inspections');
    } finally {
      setLoading(false);
    }
  };

  // Handle pre-selection from cross-navigation
  useEffect(() => {
    if (location.state?.po_id && poOptions.length > 0) {
      const targetPO = poOptions.find(opt => Number(opt.value) === Number(location.state.po_id));
      if (targetPO) {
        handlePOChange(targetPO);
      }
    }
  }, [location.state, poOptions]);


  const fetchRecentInspections = async () => {
    try {
      const response = await api.get('/inspections', { params: { per_page: 5 } });
      if (response.data.success) {
        setRecentInspections(response.data.data.data || response.data.data);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
    }
  };

  const fetchInspectionHistory = async () => {
    try {
      const response = await api.get(`/inspections/${id}/history`);
      if (response.data.success) {
        // The endpoint returns an array of history objects inside data.history
        setRecentInspections(response.data.data.history || []);
      }
    } catch (error) {
      console.error('Error fetching inspection history:', error);
    }
  };

  const handlePOChange = (option) => {
    setSelectedPO(option);
    if (!option) {
      setPoDetails(null);
      return;
    }

    // Efficiently use the data already provided in the PO list (including vendor and project)
    setPoDetails(option);
  };

  const handleDateChange = (name, date) => {
    let formattedDate = '';
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }
    setFormData(prev => ({
      ...prev,
      [name]: formattedDate
    }));
  };

  const handleSubmit = async () => {
    if (submitLoading) return;
    if (!selectedPO) {
      toast.error('Please select a PO (Purchase Order) first');
      return;
    }

    setSubmitLoading(true);
    const payload = {
      purchase_order_id: selectedPO.value,
      project_id: poDetails?.project_id,
      vendor_id: poDetails?.vendor_id,
      agency_type: formData.agency_type,
      contact_person_name: formData.contact_person_name,
      notified_date: formData.notified_date,
      actual_inspection_date: formData.actual_inspection_date,
      remarks: formData.remarks
    };

    try {
      let response;
      if (isEditMode) {
        response = await api.put(`/inspections/${id}`, payload);
      } else {
        response = await api.post('/inspections', payload);
      }

      if (response.data.success) {
        toast.success(isEditMode ? 'Inspection updated successfully!' : 'Inspection scheduled successfully!');
        navigate('/inspections');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'schedule'} inspection`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const agencyOptions = [
    { value: 'SMC', label: 'SMC (Surat Municipal Corporation)' },
    { value: 'Consultant', label: 'Consultant' },
    { value: 'Both', label: 'Both (SMC & Consultant)' }
  ];

  if (loading && isEditMode) {
    return (
      <div className={`${styles['schedule-inspection-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '20px' }}>
            <Loader2 size={48} className={styles['spinner']} color="#f59e0b" />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Loading inspection details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && !selectedPO && !loading) {
    return <NotFound />;
  }

  return (
    <div className={`${styles['schedule-inspection-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <header className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>{isEditMode ? 'Edit Schedule Inspection' : 'Schedule Inspection'}</h1>
              <p>Raise material inspection call and notify concerned authorities.</p>
            </div>

            <div className={styles['po-selector-wrapper']}>
              <label>Select PO (Purchase Order)</label>
              <CustomSelect
                options={poOptions}
                value={selectedPO}
                onChange={handlePOChange}
                placeholder="Search PO Number..."
                isSearchable={true}
                isDisabled={false}
                isLoading={isPoLoading}
                onMenuScrollToBottom={handlePOScrollToBottom}
              />
            </div>
          </header>

          <section className={`${styles['summary-grid']} ${styles['full-width']}`}>
            <div className={`${styles['summary-card']} ${!poDetails ? styles['summary-empty'] : ''} ${loading ? styles['loading'] : ''} ${styles['is-full-width']}`}>
              {!poDetails ? (
                <div className={styles['empty-state-content']}>
                  {loading ? (
                    <Loader2 size={32} className={`${styles['text-orange']} ${styles['spinner']}`} />
                  ) : (
                    <AlertCircle size={32} className={styles['text-orange']} />
                  )}
                  <p>{loading ? 'Loading PO details...' : 'Please select a PO (Purchase Order) to view details'}</p>
                </div>
              ) : (
                <>
                  <div className={styles['summary-content']}>
                    <span className={styles['summary-label']}>PO (Purchase Order) Summary</span>
                    <h2 className={styles['summary-title']}>{poDetails.po_number}</h2>

                    <div className={styles['summary-details']}>
                      <div className={styles['detail-item']}>
                        <label>Vendor</label>
                        <span>{poDetails.vendor?.vendor_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <label>Value</label>
                        <span>₹ {parseFloat(poDetails.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <label>Project</label>
                        <span>{poDetails.project?.project_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <label>PO Date</label>
                        <span>{new Date(poDetails.po_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles['summary-image-area']}>
                    <img
                      src={poDetails.po_document_filepath
                        ? getStorageUrl(poDetails.po_document_filepath)
                        : "/assets/materials_inspection.png"}
                      alt="PO Document"
                    />
                    <div className={styles['image-overlay']}>
                      <span className={styles['image-badge']}>
                        <CheckCircle2 size={12} color="#f59e0b" />
                        Materials Ready
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>


          </section>

          <section className={styles['form-section']}>
            <div className={styles['section-header']}>
              <History size={18} className={styles['section-title-icon']} />
              <h3>New Inspection Details</h3>
            </div>

            <div className={styles['form-body']}>
              <div className={styles['form-grid']}>
                <div className={styles['form-group']}>
                  <label>Inspection Agency Type</label>
                  <CustomSelect
                    options={agencyOptions}
                    value={agencyOptions.find(opt => opt.value === formData.agency_type)}
                    onChange={(opt) => setFormData(prev => ({ ...prev, agency_type: opt.value }))}
                  />
                </div>
                <div className={styles['form-group']}>
                  <label>Contact Person Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={formData.contact_person_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person_name: e.target.value }))}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label>Proposed  Inspection Date</label>
                  <CustomDatePicker
                    selected={formData.notified_date}
                    onChange={(date) => handleDateChange('notified_date', date)}
                    placeholderText="Select notification date"
                  />
                </div>

                <div className={styles['form-group']}>
                  <label>Final Inspection Date</label>
                  <CustomDatePicker
                    selected={formData.actual_inspection_date}
                    onChange={(date) => handleDateChange('actual_inspection_date', date)}
                    placeholderText="mm/dd/yyyy"
                  />
                </div>



                <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                  <label>Inspection Remarks</label>
                  <textarea
                    placeholder="Enter details about material availability, location, and specific requirements..."
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          {isEditMode && (
            <section className={styles['log-section']}>
              <div className={styles['log-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Clock size={16} color="#fcc419" />
                  <h3 style={{ fontSize: '1rem', fontWeight: '700' }}>Inspection History Log</h3>
                </div>
                <button className={styles['view-all-btn']}>View All History</button>
              </div>

              {!isMobile ? (
                <table className={styles['log-table']}>
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>TIMESTAMP</th>
                      <th>USER</th>
                      <th>CHANGES</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInspections.length > 0 ? recentInspections.map((log, index) => (
                      <tr key={log.id || index}>
                        <td style={{ fontWeight: '700', color: '#1e293b' }}>#{log.id || index + 1000}</td>
                        <td>{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                        <td><span className={styles['text-orange']} style={{ fontWeight: 600 }}>{log.user}</span></td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {log.action === 'create' ? 'Inspection Scheduled' :
                            log.action === 'update' ?
                              (log.changes?.changes && Object.keys(log.changes.changes).length > 0
                                ? Object.entries(log.changes.changes).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v.old || 'empty'} → ${v.new || 'empty'}`).join(' | ')
                                : 'no dates were changed')
                              : 'Record Updated'}
                        </td>
                        <td>
                          <span className={`${styles['status-pill']} ${log.action === 'create' ? styles['pill-completed'] : styles['pill-pending']}`}>
                            {(log.action || 'UPDATE').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" className={styles['empty-log-cell']}>No inspection history found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <div className={styles['log-cards']}>
                  {recentInspections.length > 0 ? recentInspections.map((log, index) => (
                    <div key={log.id || index} className={styles['log-card']}>
                      <div className={styles['card-header-sub']}>
                        <span className={styles['card-id']}>#{log.id || index + 1000}</span>
                        <span className={`${styles['status-pill']} ${log.action === 'create' ? styles['pill-completed'] : styles['pill-pending']}`}>
                          {(log.action || 'UPDATE').toUpperCase()}
                        </span>
                      </div>
                      <div className={styles['card-body-sub']}>
                        <div className={styles['card-info-item']}>
                          <label>Timestamp</label>
                          <span>{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                        </div>
                        <div className={styles['card-info-item']}>
                          <label>User</label>
                          <span className={styles['text-orange']} style={{ fontWeight: 600 }}>{log.user}</span>
                        </div>
                        <div className={styles['card-info-item']}>
                          <label>Changes</label>
                          <span>
                            {log.action === 'create' ? 'Scheduled' :
                              log.action === 'update' ?
                                (log.changes?.changes && Object.keys(log.changes.changes).length > 0
                                  ? Object.entries(log.changes.changes).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v.old || 'empty'} → ${v.new || 'empty'}`).join(' | ')
                                  : 'no dates were changed')
                                : 'Updated'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className={styles['empty-log-cell']}>No inspection history found</div>
                  )}
                </div>
              )}
            </section>
          )}

          <footer className={styles['form-footer']}>
            <button className={styles['btn-cancel']} onClick={() => navigate(-1)}>
              {/* <X size={18} /> */}
              <span>Cancel</span>
            </button>
            <button
              className={styles['btn-save']}
              onClick={handleSubmit}
            >
              {submitLoading ? (
                <Loader2 className={styles['spinner']} size={18} />
              ) : isEditMode ? (
                <CheckCircle2 size={18} />
              ) : (
                <Save size={18} />
              )}
              <span>{submitLoading ? 'Saving...' : isEditMode ? 'Update Inspection' : 'Save Inspection'}</span>
            </button>
          </footer>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default ScheduleInspection;
